import { useEffect, useState } from 'react';
import { api, hasPermission } from '../lib/api';
import { useAuth } from '../lib/auth';
import { StatusBadge } from '../components/StatusBadge';
import { Cloud, Mail, Shield, RefreshCw, UserPlus, Copy, Check } from 'lucide-react';

interface ExternalUser {
  id: string;
  email: string;
  role: string;
  status: string;
  totp_enabled: boolean;
  created_at: string;
  last_login_at: string | null;
  account_count?: number;
}

type Tab = 'cloud' | 'mail' | 'audit';

interface AuditEntry {
  id: string;
  action: string;
  createdAt: string;
  ipAddress: string | null;
  user: { email: string } | null;
  metadata: Record<string, unknown> | null;
}

export function AdminPage() {
  const { me } = useAuth();
  const perms = me?.permissions;
  const [tab, setTab] = useState<Tab>('cloud');
  const [cloudUsers, setCloudUsers] = useState<ExternalUser[]>([]);
  const [mailUsers, setMailUsers] = useState<ExternalUser[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(false);

  // Create-Account Dialog
  const [showCreate, setShowCreate] = useState<'cloud' | 'mail' | null>(null);
  const [createEmail, setCreateEmail] = useState('');
  const [createQuota, setCreateQuota] = useState(10);
  const [createdPassword, setCreatedPassword] = useState('');
  const [createError, setCreateError] = useState('');
  const [copied, setCopied] = useState(false);

  const loadData = async (t: Tab) => {
    setLoading(true);
    try {
      if (t === 'cloud') {
        const res = await api.get<{ users: ExternalUser[] }>('/admin/cloud/users');
        setCloudUsers(res.users);
      } else if (t === 'mail') {
        const res = await api.get<{ users: ExternalUser[] }>('/admin/mail/users');
        setMailUsers(res.users);
      } else {
        const res = await api.get<{ logs: AuditEntry[] }>('/admin/audit');
        setAuditLogs(res.logs);
      }
    } catch { /* handled by empty state */ }
    setLoading(false);
  };

  useEffect(() => { void loadData(tab); }, [tab]);

  async function handleCreateAccount(e: React.FormEvent) {
    e.preventDefault();
    setCreateError('');
    try {
      const endpoint = showCreate === 'cloud' ? '/admin/cloud/users' : '/admin/mail/users';
      const body = showCreate === 'cloud'
        ? { email: createEmail, storageQuotaGb: createQuota }
        : { email: createEmail };
      const res = await api.post<{ initialPassword: string }>(endpoint, body);
      setCreatedPassword(res.initialPassword);
      void loadData(showCreate!);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Fehler');
    }
  }

  function copyToClipboard(text: string) {
    void navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const canCreateCloud = hasPermission(perms, 'cloud.users.create');
  const canCreateMail = hasPermission(perms, 'mail.users.create');

  const tabs: { key: Tab; label: string; icon: typeof Cloud }[] = [
    { key: 'cloud', label: 'Cloud-User', icon: Cloud },
    { key: 'mail', label: 'Mail-User', icon: Mail },
    { key: 'audit', label: 'Audit-Log', icon: Shield },
  ];

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Admin-Zentrale</h1>
          <p className="text-sm text-slate-500 mt-1">Cloud- und Mail-Accounts verwalten</p>
        </div>
        <div className="flex gap-2">
          {tab === 'cloud' && canCreateCloud && (
            <button
              onClick={() => { setShowCreate('cloud'); setCreateEmail(''); setCreatedPassword(''); setCreateError(''); setCreateQuota(10); }}
              className="btn btn-primary text-sm flex items-center gap-2"
            >
              <UserPlus size={14} /> Cloud-Konto
            </button>
          )}
          {tab === 'mail' && canCreateMail && (
            <button
              onClick={() => { setShowCreate('mail'); setCreateEmail(''); setCreatedPassword(''); setCreateError(''); }}
              className="btn btn-primary text-sm flex items-center gap-2"
            >
              <UserPlus size={14} /> Mail-Konto
            </button>
          )}
          <button onClick={() => loadData(tab)} className="btn text-sm flex items-center" disabled={loading}>
            <RefreshCw size={14} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition flex-1 justify-center ${
              tab === key ? 'bg-white shadow-sm text-rla-500' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-32 text-slate-500">Laden ...</div>
      ) : tab === 'cloud' ? (
        <UserTable users={cloudUsers} type="cloud" />
      ) : tab === 'mail' ? (
        <UserTable users={mailUsers} type="mail" />
      ) : (
        <AuditTable logs={auditLogs} />
      )}

      {/* Create Account Dialog */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => !createdPassword && setShowCreate(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            {createdPassword ? (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-slate-800">
                  {showCreate === 'cloud' ? 'Cloud' : 'Mail'}-Konto erstellt
                </h2>
                <p className="text-sm text-slate-600">Initial-Passwort (wird nur einmal gezeigt!):</p>
                <div className="flex items-center gap-2">
                  <code className="bg-slate-100 px-3 py-2 rounded border text-sm font-mono flex-1">{createdPassword}</code>
                  <button onClick={() => copyToClipboard(createdPassword)} className="btn btn-primary px-3 py-2">
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
                <button onClick={() => setShowCreate(null)} className="btn btn-primary w-full">Schließen</button>
              </div>
            ) : (
              <form onSubmit={(e) => void handleCreateAccount(e)} className="space-y-4">
                <h2 className="text-lg font-semibold text-slate-800">
                  Neues {showCreate === 'cloud' ? 'Cloud' : 'Mail'}-Konto
                </h2>
                {createError && <div className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{createError}</div>}
                <div>
                  <label className="label">E-Mail</label>
                  <input type="email" required value={createEmail} onChange={(e) => setCreateEmail(e.target.value)}
                    className="input" placeholder="name@example.de" />
                </div>
                {showCreate === 'cloud' && (
                  <div>
                    <label className="label">Speicher-Kontingent (GB)</label>
                    <input type="number" min={1} max={100} value={createQuota} onChange={(e) => setCreateQuota(Number(e.target.value))}
                      className="input" />
                  </div>
                )}
                <div className="flex gap-3 justify-end">
                  <button type="button" onClick={() => setShowCreate(null)} className="btn">Abbrechen</button>
                  <button type="submit" className="btn btn-primary">Erstellen</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function UserTable({ users, type }: { users: ExternalUser[]; type: 'cloud' | 'mail' }) {
  if (users.length === 0) {
    return <div className="card text-center text-slate-500 py-8">Keine User gefunden</div>;
  }

  return (
    <div className="card overflow-x-auto p-0">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50">
            <th className="text-left px-4 py-3 font-medium text-slate-600">E-Mail</th>
            <th className="text-left px-4 py-3 font-medium text-slate-600">Rolle</th>
            <th className="text-left px-4 py-3 font-medium text-slate-600">Status</th>
            <th className="text-left px-4 py-3 font-medium text-slate-600">2FA</th>
            {type === 'mail' && (
              <th className="text-left px-4 py-3 font-medium text-slate-600">Konten</th>
            )}
            <th className="text-left px-4 py-3 font-medium text-slate-600">Letzter Login</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50 transition">
              <td className="px-4 py-3 font-medium">{u.email}</td>
              <td className="px-4 py-3 capitalize">{u.role.replace('_', ' ')}</td>
              <td className="px-4 py-3">
                <StatusBadge status={u.status as 'active' | 'locked' | 'disabled'} />
              </td>
              <td className="px-4 py-3">
                <span className={`text-xs font-medium ${u.totp_enabled ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {u.totp_enabled ? 'Aktiv' : 'Aus'}
                </span>
              </td>
              {type === 'mail' && (
                <td className="px-4 py-3">{u.account_count ?? 0}</td>
              )}
              <td className="px-4 py-3 text-slate-500">
                {u.last_login_at
                  ? new Date(u.last_login_at).toLocaleString('de-DE')
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditTable({ logs }: { logs: AuditEntry[] }) {
  if (logs.length === 0) {
    return <div className="card text-center text-slate-500 py-8">Keine Einträge</div>;
  }

  return (
    <div className="card overflow-x-auto p-0">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50">
            <th className="text-left px-4 py-3 font-medium text-slate-600">Zeitpunkt</th>
            <th className="text-left px-4 py-3 font-medium text-slate-600">Aktion</th>
            <th className="text-left px-4 py-3 font-medium text-slate-600">User</th>
            <th className="text-left px-4 py-3 font-medium text-slate-600">IP</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((l) => (
            <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50 transition">
              <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                {new Date(l.createdAt).toLocaleString('de-DE')}
              </td>
              <td className="px-4 py-3 font-mono text-xs">{l.action}</td>
              <td className="px-4 py-3">{l.user?.email ?? '—'}</td>
              <td className="px-4 py-3 text-slate-500 font-mono text-xs">{l.ipAddress ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
